import { Column, Table as DatumTable } from '@datum-group/isogd-openapi/lib';
import {
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';
import {
  GeoJSONRecordWithAttachmentAndDisplay,
  TableAPIService,
} from './TableAPIService';
import { TableQuery, TableQueryOptions } from './TableQuery';

interface TableOptions {
  columns: Column[];
  info: DatumTable;
  total: number;
  records: GeoJSONRecordWithAttachmentAndDisplay[];
  initialQuery: TableQueryOptions;
}

export class Table {
  @observable private _query: TableQuery;
  @observable private _columns: Column[];
  @observable private _info: DatumTable;
  @observable private _total: number;
  @observable private _records: GeoJSONRecordWithAttachmentAndDisplay[];

  constructor({ columns, records, info, total, initialQuery }: TableOptions) {
    this._query = new TableQuery(initialQuery);
    this._columns = columns;
    this._info = info;
    this._total = total;
    this._records = records;

    makeObservable<Table>(this);
  }

  @computed get query() {
    return this._query;
  }

  @computed get records() {
    return this._records;
  }

  @computed get total() {
    return this._total;
  }

  @computed get columns() {
    return this._columns;
  }

  @computed get info() {
    return this._info;
  }

  set records(value: GeoJSONRecordWithAttachmentAndDisplay[]) {
    this._records = value;
  }

  set total(value: number) {
    this._total = value;
  }
}

interface TableAPIControllerOptions {
  table: Table;
}

export class TableAPIController {
  private _service: TableAPIService;
  private _disposer?: IReactionDisposer;

  readonly table: Table;

  @observable private _isLoading = false;
  @observable private _error?: string = undefined;

  constructor({ table }: TableAPIControllerOptions) {
    this._service = new TableAPIService();
    this.table = table;

    makeObservable<TableAPIController>(this);
  }

  @computed get isLoading() {
    return this._isLoading;
  }

  @computed get error() {
    return this._error;
  }

  bind = () => {
    this.table.query.bind();

    this._disposer = reaction(
      () => ({
        page: this.table.query.page,
        pageSize: this.table.query.pageSize,
        filterFields: this.table.query.filterFields,
        order: this.table.query.order,
      }),
      async (query) => {
        await this._loadRecords(query);
      },
    );

    return this.unbind;
  };

  unbind = () => {
    this.table.query.unbind();
    this._disposer?.();
    this._disposer = undefined;
  };

  private _loadRecords = async (query: TableQueryOptions) => {
    runInAction(() => {
      this._isLoading = true;
      this._error = undefined;
      this.table.records = [];
    });

    try {
      const records = await this._service.loadRecords(
        this.table.info.table_name,
        query,
        this.table.columns,
      );

      runInAction(() => {
        this.table.records = records.features;
        this.table.total = records._count;
      });
    } catch (e) {
      const fallbackMsg = `Не удалось загрузить таблицу ${this.table.info.table_name}`;

      const error = e instanceof Error ? e.message : fallbackMsg;

      runInAction(() => {
        this._error = error;
      });
    }

    runInAction(() => {
      this._isLoading = false;
    });
  };
}
